import { hexToRgb, rgbToHex, rgbToHsl } from "@/lib/color-utils";

/**
 * Color Grabber — automatic, perceptual color extraction.
 *
 * Unlike a pixel eyedropper (which returns the exact hex under a single
 * point), this scans every sampled pixel in the photo and groups them by the
 * human-perceived color family they belong to (Red, Blue, Green, Brown, …)
 * regardless of shade, shadow, or highlight. A deep maroon and a bright
 * cherry both count as "Red" — the same way a person looking at the photo
 * would describe it — while still keeping the individual hex shades on hand
 * as supporting detail.
 */

const SAMPLE_SIZE = 220;
const QUANT = 12; // finer than the aura sampler — we need enough shade detail to group well

export type ColorName =
  | "Red" | "Orange" | "Yellow" | "Brown" | "Green" | "Teal"
  | "Blue" | "Purple" | "Pink" | "White" | "Gray" | "Black";

export interface ColorShade {
  hex: string;
  /** Share of the whole image this exact shade represents, 0-1. */
  weight: number;
}

export interface GrabbedColorGroup {
  name: ColorName;
  /** Most representative hex for this named color (the most common shade within it). */
  hex: string;
  /** Combined share of the image covered by every shade of this color, 0-1. */
  weight: number;
  /** The distinct shades detected within this named color, most common first. */
  shades: ColorShade[];
}

export type Temperature = "warm" | "cool" | "neutral";
export type PaletteType = "vibrant" | "pastel" | "muted" | "dark" | "monochrome" | "balanced";
export type Harmony = "Monochromatic" | "Complementary" | "Triadic" | "Analogous" | "Multicolor";

export interface ColorReport {
  groups: GrabbedColorGroup[];
  totalSampled: number;
  dominant: GrabbedColorGroup;
  temperature: Temperature;
  warmShare: number; // 0-1
  coolShare: number; // 0-1
  neutralShare: number; // 0-1
  avgBrightness: number; // 0-100
  avgSaturation: number; // 0-100
  paletteType: PaletteType;
  harmony: Harmony;
  complementaryHex: string;
  contrastText: "#FFFFFF" | "#1A1A1A";
  moodSummary: string;
}

/**
 * The "textbook" pure hex for each named color family — the red everyone
 * pictures when they hear "red", regardless of how muted, dark, or bright
 * the actual detected shade was in the photo.
 */
export const ABSOLUTE_HEX: Record<ColorName, string> = {
  Red: "#EE1111",
  Orange: "#FF7F11",
  Yellow: "#FFD500",
  Brown: "#8B4A2B",
  Green: "#1FA12E",
  Teal: "#00A0A0",
  Blue: "#1959D1",
  Purple: "#8A2BBF",
  Pink: "#FF4FA3",
  White: "#FFFFFF",
  Gray: "#8C8C8C",
  Black: "#151515",
};

const WARM: ColorName[] = ["Red", "Orange", "Yellow", "Brown"];
const COOL: ColorName[] = ["Green", "Teal", "Blue", "Purple", "Pink"];
const NEUTRAL: ColorName[] = ["White", "Gray", "Black"];

/**
 * Maps an RGB sample to the color name a person would use to describe it —
 * grouping by hue family first, then carving out achromatic (gray/black/
 * white) and desaturated warm (brown) special cases that hue alone would
 * misclassify.
 */
export function nameColor(r: number, g: number, b: number): ColorName {
  const { h, s, l } = rgbToHsl(r, g, b);

  if (l >= 92) return "White";
  if (l <= 10) return "Black";
  if (s <= 10) return "Gray";

  // Pink: a light, gentle tint of red/magenta — skin tones, pastel rose,
  // salmon. Human eyes call these "pink", not "red", precisely because
  // they're light and desaturated rather than bold and saturated.
  const isRedishHue = h >= 330 || h <= 20;
  const isMagentaHue = h > 302 && h < 330;
  if ((isRedishHue || isMagentaHue) && l >= 55 && s >= 12 && s <= 85) {
    return "Pink";
  }

  // Brown: warm hue, but muted/dark enough that it reads as earthy rather
  // than a vivid orange or red (a campfire ember vs. a rust-colored rock).
  if (h >= 5 && h <= 50 && s <= 65 && l <= 55 && l >= 12) {
    if (s <= 45 || l <= 38) return "Brown";
  }

  if (h < 12 || h >= 348) return "Red";
  if (h < 42) return "Orange";
  if (h < 66) return "Yellow";
  if (h < 163) return "Green";
  if (h < 195) return "Teal";
  if (h < 258) return "Blue";
  if (h < 302) return "Purple";
  return "Pink";
}

function hueOf(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHsl(r, g, b).h;
}

function circularHueDistance(a: number, b: number): number {
  const d = Math.abs(a - b);
  return Math.min(d, 360 - d);
}

/**
 * Scans an image and returns every human-named color present, each with its
 * coverage share and the underlying hex shades that were grouped into it,
 * plus a full analysis of the palette as a whole (temperature, brightness,
 * saturation, harmony, mood, and a suggested complementary color). Runs
 * entirely client-side — the photo is never uploaded anywhere.
 */
export function buildColorReport(image: HTMLImageElement): ColorReport | null {
  const canvas = document.createElement("canvas");
  canvas.width = SAMPLE_SIZE;
  canvas.height = SAMPLE_SIZE;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;

  ctx.drawImage(image, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
  const { data } = ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE);

  // bucket exact shades (for the "shades" breakdown) and running sums for
  // brightness/saturation, all in a single pass.
  const shadeBuckets = new Map<string, { r: number; g: number; b: number; count: number; name: ColorName }>();
  let totalSampled = 0;
  let brightnessSum = 0;
  let saturationSum = 0;

  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3];
    if (alpha < 128) continue;
    const r = Math.round(data[i] / QUANT) * QUANT;
    const g = Math.round(data[i + 1] / QUANT) * QUANT;
    const b = Math.round(data[i + 2] / QUANT) * QUANT;
    const key = `${r},${g},${b}`;
    let entry = shadeBuckets.get(key);
    if (!entry) {
      entry = { r, g, b, count: 0, name: nameColor(r, g, b) };
      shadeBuckets.set(key, entry);
    }
    entry.count += 1;
    totalSampled += 1;
    const { s, l } = rgbToHsl(data[i], data[i + 1], data[i + 2]);
    brightnessSum += l;
    saturationSum += s;
  }

  if (totalSampled === 0) return null;

  // Group shade buckets by their perceptual name.
  const groupMap = new Map<ColorName, { count: number; shades: { hex: string; count: number }[] }>();
  for (const bucket of shadeBuckets.values()) {
    const hex = rgbToHex(bucket.r, bucket.g, bucket.b);
    let group = groupMap.get(bucket.name);
    if (!group) {
      group = { count: 0, shades: [] };
      groupMap.set(bucket.name, group);
    }
    group.count += bucket.count;
    group.shades.push({ hex, count: bucket.count });
  }

  const groups: GrabbedColorGroup[] = Array.from(groupMap.entries())
    .map(([name, g]) => {
      const shades = g.shades.sort((a, b) => b.count - a.count);
      return {
        name,
        hex: shades[0].hex,
        weight: g.count / totalSampled,
        shades: shades.slice(0, 6).map((s) => ({ hex: s.hex, weight: s.count / totalSampled })),
      };
    })
    .sort((a, b) => b.weight - a.weight)
    // Drop specks of color that don't meaningfully register to the eye,
    // but never drop below at least one group. Thin details (outlines,
    // small accents) can still be a real, visible color at low coverage,
    // so the bar here is low.
    .filter((g, i) => i === 0 || g.weight >= 0.006);

  const dominant = groups[0];

  const warmShare = groups.filter((g) => WARM.includes(g.name)).reduce((s, g) => s + g.weight, 0);
  const coolShare = groups.filter((g) => COOL.includes(g.name)).reduce((s, g) => s + g.weight, 0);
  const neutralShare = groups.filter((g) => NEUTRAL.includes(g.name)).reduce((s, g) => s + g.weight, 0);

  let temperature: Temperature;
  if (neutralShare >= 0.6) temperature = "neutral";
  else temperature = warmShare >= coolShare ? "warm" : "cool";

  const avgBrightness = Math.round(brightnessSum / totalSampled);
  const avgSaturation = Math.round(saturationSum / totalSampled);

  let paletteType: PaletteType;
  if (avgSaturation <= 15) paletteType = "monochrome";
  else if (avgBrightness <= 32) paletteType = "dark";
  else if (avgBrightness >= 75 && avgSaturation <= 48) paletteType = "pastel";
  else if (avgSaturation >= 55) paletteType = "vibrant";
  else paletteType = "muted";

  // Harmony: look at how the hues of the significant chromatic groups relate.
  const chromaticGroups = groups.filter((g) => !NEUTRAL.includes(g.name));
  const hues = chromaticGroups.map((g) => hueOf(g.hex));
  let harmony: Harmony;
  if (hues.length <= 1) {
    harmony = "Monochromatic";
  } else {
    const spread = Math.max(...hues) - Math.min(...hues);
    const hasComplement = hues.some((h1, i) =>
      hues.some((h2, j) => i !== j && circularHueDistance(h1, h2) >= 150),
    );
    if (hues.length === 2 && hasComplement) harmony = "Complementary";
    else if (hues.length >= 3 && hues.length <= 4 && spread <= 320 && hasComplement) harmony = "Triadic";
    else if (spread <= 60) harmony = "Analogous";
    else harmony = "Multicolor";
  }

  const dominantHue = hueOf(dominant.hex);
  const complementaryHex = hslShiftToHex(dominantHue, avgSaturation, avgBrightness);

  const contrastText = luminanceOf(dominant.hex) > 0.42 ? "#1A1A1A" : "#FFFFFF";

  const moodSummary = buildMoodSummary({ temperature, paletteType, harmony, dominant });

  return {
    groups,
    totalSampled,
    dominant,
    temperature,
    warmShare,
    coolShare,
    neutralShare,
    avgBrightness,
    avgSaturation,
    paletteType,
    harmony,
    complementaryHex,
    contrastText,
    moodSummary,
  };
}

function hslShiftToHex(h: number, s: number, l: number): string {
  const shifted = (h + 180) % 360;
  const sat = Math.min(90, Math.max(35, s));
  const light = Math.min(72, Math.max(30, l));
  const c = (1 - Math.abs(2 * (light / 100) - 1)) * (sat / 100);
  const x = c * (1 - Math.abs(((shifted / 60) % 2) - 1));
  const m = light / 100 - c / 2;
  let r = 0, g = 0, b = 0;
  if (shifted < 60) { r = c; g = x; b = 0; }
  else if (shifted < 120) { r = x; g = c; b = 0; }
  else if (shifted < 180) { r = 0; g = c; b = x; }
  else if (shifted < 240) { r = 0; g = x; b = c; }
  else if (shifted < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }
  return rgbToHex((r + m) * 255, (g + m) * 255, (b + m) * 255);
}

function luminanceOf(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const lin = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

const TEMPERATURE_WORDS: Record<Temperature, string> = {
  warm: "warm",
  cool: "cool",
  neutral: "neutral, low-color",
};

const PALETTE_WORDS: Record<PaletteType, string> = {
  vibrant: "vibrant and saturated",
  pastel: "soft and pastel",
  muted: "muted and understated",
  dark: "dark and moody",
  monochrome: "nearly monochrome",
  balanced: "balanced",
};

function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

/** Plain-data version of the report, shaped for a JSON download — full
 * fidelity, meant for developers or anyone piping the result into other
 * tools/scripts. */
export function buildJsonExport(report: ColorReport) {
  return {
    colors: report.groups.map((g) => ({
      name: g.name,
      hex: g.hex,
      trueColorHex: ABSOLUTE_HEX[g.name],
      coveragePercent: Math.round(g.weight * 1000) / 10,
      shades: g.shades.map((s) => ({ hex: s.hex, coveragePercent: Math.round(s.weight * 1000) / 10 })),
    })),
    analysis: {
      dominantColor: report.dominant.name,
      dominantHex: report.dominant.hex,
      temperature: report.temperature,
      paletteType: report.paletteType,
      harmony: report.harmony,
      avgBrightness: report.avgBrightness,
      avgSaturation: report.avgSaturation,
      complementaryHex: report.complementaryHex,
    },
  };
}

/** Spreadsheet-friendly CSV — one row per detected color, meant for pasting
 * into Excel/Sheets/Airtable or a design spec doc. */
export function buildCsvExport(report: ColorReport): string {
  const header = ["Color Name", "Hex (Detected)", "Hex (True Color)", "Coverage %", "Shades Detected"];
  const rows = report.groups.map((g) => [
    g.name,
    g.hex,
    ABSOLUTE_HEX[g.name],
    (Math.round(g.weight * 1000) / 10).toString(),
    g.shades.map((s) => s.hex).join(" "),
  ]);
  const escape = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  return [header, ...rows].map((row) => row.map(escape).join(",")).join("\n");
}

/** CSS custom-property sheet, ready to drop into a stylesheet — meant for
 * web/product designers who want to reuse the extracted palette directly in
 * their own project. Includes both the exact detected shade and the pure
 * "true color" version of each named color. */
export function buildCssExport(report: ColorReport): string {
  const lines = [
    "/* Generated by Mychromamind Color Grabber */",
    "/* --color-<name>: the exact shade detected in your photo */",
    "/* --color-<name>-true: the pure, textbook version of that color */",
    ":root {",
  ];
  for (const g of report.groups) {
    const slug = slugify(g.name);
    lines.push(`  --color-${slug}: ${g.hex};`);
    lines.push(`  --color-${slug}-true: ${ABSOLUTE_HEX[g.name]};`);
  }
  lines.push(`  --color-dominant: ${report.dominant.hex};`);
  lines.push(`  --color-complementary: ${report.complementaryHex};`);
  lines.push("}");
  return lines.join("\n");
}

function buildMoodSummary({
  temperature,
  paletteType,
  harmony,
  dominant,
}: {
  temperature: Temperature;
  paletteType: PaletteType;
  harmony: Harmony;
  dominant: GrabbedColorGroup;
}): string {
  const harmonyLine: Record<Harmony, string> = {
    Monochromatic: "It leans on a single color family for a cohesive, unified feel.",
    Complementary: "It pairs colors from opposite ends of the color wheel, giving it natural visual contrast.",
    Triadic: "It draws from colors spaced evenly around the wheel, giving it a lively, well-rounded balance.",
    Analogous: "Its colors sit close together on the wheel, giving it a smooth, harmonious flow.",
    Multicolor: "It spans a wide range of hues, giving it an eclectic, eye-catching mix.",
  };
  return `This photo reads as ${TEMPERATURE_WORDS[temperature]} and ${PALETTE_WORDS[paletteType]}, dominated by ${dominant.name.toLowerCase()}. ${harmonyLine[harmony]}`;
}
